import { useMutation, useQueryClient } from '@tanstack/react-query';

import solomonService from 'services/solomonService';

import type { SolomonAcceptActionRequest, SolomonAcceptActionResponse, SolomonPageContext } from '../../types/solomon';

interface UseSolomonAcceptActionProps {
  artistId: string;
  pageContext: SolomonPageContext;
  onSuccess?: (data: SolomonAcceptActionResponse, variables: SolomonAcceptActionRequest) => void;
  onError?: (error: Error, variables: SolomonAcceptActionRequest) => void;
}

export function useSolomonAcceptAction({ artistId, pageContext, onSuccess, onError }: UseSolomonAcceptActionProps) {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (request: SolomonAcceptActionRequest) => {
      return solomonService.acceptAction({
        artistId,
        ...request,
      });
    },
    onSuccess: (data, variables) => {
      if (data.success) {
        // Invalidate artist queries to refresh the data
        queryClient.invalidateQueries({
          queryKey: ['artist', artistId],
        });
        // Also invalidate cover arts if relevant
        queryClient.invalidateQueries({
          queryKey: ['coverArts', artistId],
        });
        // Invalidate recommendations since state has changed
        queryClient.invalidateQueries({
          queryKey: ['solomon-recommendations', artistId, pageContext],
        });
      }
      onSuccess?.(data, variables);
    },
    onError: (error: Error, variables) => {
      onError?.(error, variables);
    },
  });

  return {
    acceptAction: mutation.mutateAsync,
    isAccepting: mutation.isPending,
    error: mutation.error,
    ...mutation,
  };
}

export default useSolomonAcceptAction;
